import React, { useRef, useEffect, useState } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import EmailEditor from "react-email-editor";

const TemplatePreview = ({ template, onSelect }) => {
  const emailEditorRef = useRef();
  const [previewHtml, setPreviewHtml] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const handleLoad = () => {
   
    if (emailEditorRef.current && emailEditorRef.current.editor) {
      // Load the template design first
      emailEditorRef.current.editor.loadDesign(template.design);
      
      // Wait a bit for the design to load, then export HTML
      setTimeout(() => {
        if (emailEditorRef.current && emailEditorRef.current.editor) {
          emailEditorRef.current.editor.exportHtml((data) => {
            
            // Replace merge tags with sample values for preview
            let previewHtml = data.html;
            
            // Define sample values for merge tags
            const sampleValues = {
              '{contactfield=firstname}': 'John',
              '{contactfield=lastname}': 'Doe',
              '{contactfield=last_added_item_name}': 'Premium Product 250mL',
              '{contactfield=last_added_item_image}': 'https://cdn.saucey.com/direct/web/crm-placehold-product.png',
              '{contactfield=cart_items_count}': '3',
              '{contactfield=cart_total}': '$149.99',
              '{contactfield=store_name}': 'Your Store',
              '{contactfield=store_url}': 'https://yourstore.com',
              '{contactfield=checkout_url}': 'https://yourstore.com/checkout',
              '{contactfield=cart_url}': 'https://yourstore.com/cart'
            };
            
            // Replace all merge tags with sample values
            Object.entries(sampleValues).forEach(([tag, value]) => {
              previewHtml = previewHtml.replace(new RegExp(tag.replace(/[{}]/g, '\\$&'), 'g'), value);
            });
            
            setPreviewHtml(previewHtml);
            setIsGenerating(false);
          });
        }
      }, 2000);
    }
  };

  const handleUseTemplate = (e) => {
    e.stopPropagation();
    onSelect(template.design);
  };

  return (
    <div
      className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors relative"
      onClick={() => onSelect(template.design)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="text-center mb-3">
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
          {template.name}
        </h3>
      </div>
      
      <div className="border border-slate-200 dark:border-slate-600 rounded overflow-hidden bg-white relative">
        <div 
          className="overflow-hidden rounded relative"
          style={{ 
            width: '100%', 
            height: '200px'
          }}
        >
          {isGenerating ? (
            <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-700 dark:to-slate-600 flex flex-col items-center justify-center rounded">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                Loading Preview
              </span>
            </div>
          ) : previewHtml ? (
            <div
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              style={{
                transform: 'scale(0.5)',
                transformOrigin: 'top left',
                width: '200%',
                height: '400px'
              }}
            />
          ) : (
            <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-700 dark:to-slate-600 flex flex-col items-center justify-center rounded">
              <div className="text-4xl mb-2">📧</div>
              <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                {template.name}
              </span>
              <span className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                Click to use this template
              </span>
            </div>
          )}
          
          {/* Hover overlay */}
          {isHovered && !isGenerating && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded transition-opacity duration-200">
              <PrimaryButton onClick={handleUseTemplate}>
                Use Template
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
      
      {/* Hidden EmailEditor for generating preview */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '600px', height: '400px' }}>
        <EmailEditor
          ref={emailEditorRef}
          minHeight={400}
          onLoad={handleLoad}
          options={{
            mergeTags: {
              first_name: {
                name: 'First Name',
                value: '{contactfield=firstname}',
                sample: 'John',
              },
              last_name: {
                name: 'Last Name',
                value: '{contactfield=lastname}',
                sample: 'Doe',
              },
              last_added_item_name: {
                name: "Last Added Item Name",
                value: "{contactfield=last_added_item_name}",
                sample: "Some Product Name",
              },
              last_added_item_image: {
                name: "Last Added Item Image",
                value: "{contactfield=last_added_item_image}",
                sample: "https://cdn.saucey.com/direct/web/crm-placehold-product.png",
              },

            },
            tools: {
              text: {
                properties: {
                  textAlign: {
                    value: 'center'
                  }
                }
              }
            },
            features: {
              devTab: false,
            },
            version: '1.157.0'
          }}
        />
      </div>
    </div>
  );
};

const BlankTemplate = ({ onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const handleUseBlank = (e) => {
    e.stopPropagation();
    onSelect(null); // Pass null for blank template
  };

  return (
    <div
      className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors relative"
      onClick={() => onSelect(null)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="text-center mb-3">
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
          Blank Template
        </h3>
      </div>
      
      <div className="border border-slate-200 dark:border-slate-600 rounded overflow-hidden bg-white relative">
        <div 
          className="overflow-hidden rounded relative"
          style={{ 
            width: '100%', 
            height: '200px'
          }}
        >
          <div className="w-full h-48 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700 dark:to-slate-600 flex flex-col items-center justify-center rounded">
            <div className="text-4xl mb-2">📄</div>
            <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">
              Start from scratch
            </span>
            <span className="text-slate-500 dark:text-slate-400 text-xs mt-1">
              Create your own design
            </span>
          </div>
          
          {/* Hover overlay */}
          {isHovered && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded transition-opacity duration-200">
              <PrimaryButton onClick={handleUseBlank}>
                Use Blank Template
              </PrimaryButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateSelectionModal = ({ 
  isOpen, 
  onClose, 
  templates, 
  onTemplateSelect 
}) => {
  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={onClose}
      title="Choose a Template"
      width="7xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Blank Template - Always first */}
        <BlankTemplate onSelect={onTemplateSelect} />
        
        {/* Regular Templates */}
        {templates.map((template) => (
          <TemplatePreview
            key={template.id}
            template={template}
            onSelect={onTemplateSelect}
          />
        ))}
      </div>
    </ModalBasic>
  );
};

export default TemplateSelectionModal;
